/**
 * CompanyDocuments.jsx - Dokumentbibliotek for bedriften
 * 
 * Viser alle dokumenter opprettet av bedriftens brukere.
 * Inkluderer slett-funksjonalitet.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  FileText, 
  Search, 
  Plus,
  FolderOpen,
  Clock,
  Download,
  Trash2,
  X,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyDocuments() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Hent dokumenter
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await axios.get(`${API}/documents`, { headers });
        setDocuments(res.data || []);
      } catch (error) {
        console.error('Failed to fetch documents:', error);
      }
      setLoading(false);
    };
    
    fetchDocuments();
  }, [token]);
  
  // Slett dokument
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/documents/${deleteConfirm.id}`, { headers });
      setDocuments(prev => prev.filter(d => d.id !== deleteConfirm.id));
      toast.success('Dokument slettet');
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Kunne ikke slette dokument');
    }
    setDeleteLoading(false);
  };
  
  // Filtrer dokumenter
  const vaultCategories = [...new Set(documents.map(d => d.vault_category).filter(Boolean))];
  
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.module?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || doc.vault_category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  
  // Format dato
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="company-documents">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dokumenter</h1>
          <p className="text-[#8899A8] mt-1">
            Alle dokumenter opprettet av bedriftens brukere
          </p>
        </div>
        <button
          onClick={() => navigate('/company/modules')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#B87333] hover:bg-[#A06328] text-white rounded-lg transition-colors font-medium"
          data-testid="new-document-btn"
        >
          <Plus className="w-4 h-4" />
          Nytt dokument
        </button>
      </div>

      {/* Søk og Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8899A8]" />
          <input
            type="text"
            placeholder="Søk i dokumenter..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#1B2631] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
            data-testid="document-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
              categoryFilter === 'all'
                ? 'border-[#B87333] text-[#B87333] bg-[#B87333]/10'
                : 'border-[#2C3E50] text-[#8899A8] hover:text-white'
            }`}
          >
            Alle
          </button>
          {vaultCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                categoryFilter === cat
                  ? 'border-[#B87333] text-[#B87333] bg-[#B87333]/10'
                  : 'border-[#2C3E50] text-[#8899A8] hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#B87333]" />
        </div>
      ) : filteredDocuments.length === 0 ? (
        /* Ingen dokumenter */
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-12 text-center">
          <FolderOpen className="w-12 h-12 text-[#8899A8] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            {searchQuery ? 'Ingen treff' : 'Ingen dokumenter ennå'}
          </h3>
          <p className="text-[#8899A8] mb-4">
            {searchQuery 
              ? 'Prøv et annet søkeord'
              : 'Opprett dokumenter fra en av modulene for å komme i gang.'
            }
          </p>
          {!searchQuery && (
            <button
              onClick={() => navigate('/company/modules')}
              className="px-4 py-2 bg-[#B87333] text-white rounded-lg hover:bg-[#A06328]"
            >
              Gå til moduler
            </button>
          )}
        </div>
      ) : (
        /* Dokumentliste */
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2C3E50]">
                <th className="text-left px-4 py-3 text-xs font-medium text-[#8899A8] uppercase tracking-wider">
                  Dokument
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-[#8899A8] uppercase tracking-wider hidden sm:table-cell">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-[#8899A8] uppercase tracking-wider hidden md:table-cell">
                  Opprettet
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-[#8899A8] uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2C3E50]">
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="hover:bg-[#2C3E50]/30 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#B87333]/20 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#B87333]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {doc.title || 'Uten tittel'}
                        </p>
                        {doc.module && (
                          <p className="text-xs text-[#8899A8] capitalize">{doc.module}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 hidden sm:table-cell">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      doc.status === 'ferdig' 
                        ? 'bg-[#22c55e]/20 text-[#22c55e]'
                        : 'bg-[#f59e0b]/20 text-[#f59e0b]'
                    }`}>
                      {doc.status === 'ferdig' ? 'Ferdig' : 'Utkast'}
                    </span>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell">
                    <div className="flex items-center gap-2 text-sm text-[#8899A8]">
                      <Clock className="w-4 h-4" />
                      {formatDate(doc.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button 
                        onClick={() => navigate(`/documents?id=${doc.id}`)}
                        className="p-2 rounded-lg hover:bg-[#2C3E50] text-[#8899A8] hover:text-white transition-colors"
                        title="Åpne dokument"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setDeleteConfirm(doc)}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-[#8899A8] hover:text-red-400 transition-colors"
                        title="Slett dokument"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1B2631] rounded-xl w-full max-w-md border border-[#2C3E50]" data-testid="delete-doc-modal">
            <div className="flex items-center justify-between p-4 border-b border-[#2C3E50]">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                Slett dokument
              </h3>
              <button 
                onClick={() => setDeleteConfirm(null)}
                className="p-1 hover:bg-[#2C3E50] rounded"
              >
                <X className="w-5 h-5 text-[#8899A8]" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-[#8899A8] mb-4">
                Er du sikker på at du vil slette dokumentet <span className="text-white font-medium">"{deleteConfirm.title || 'Uten tittel'}"</span>?
              </p>
              <div className="flex items-start gap-2 p-3 bg-red-500/10 rounded-lg text-sm text-red-400">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Denne handlingen kan ikke angres.</span>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 p-4 border-t border-[#2C3E50]">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-[#8899A8] hover:text-white"
              >
                Avbryt
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50"
                data-testid="confirm-delete-doc-btn"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Slett permanent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
